import type { Content, NormalizedContent, SearchableTicker } from "@tmi/types/entities";
import type { CompositionContext } from "@/services/data/sync-supervisor";

// Fix round 1, Finding 1 (Critical): createDefaultLogger() with NO argument defaults to
// LogLevels.ERROR — under that level, the factory's own gating (console-logger.ts) makes
// `logger.warn` an empty stub, so the drop-warn below never actually fires anywhere. Every
// sibling sync file that logs at warn/info/debug passes LogLevels.DEBUG explicitly for exactly
// this reason; this file just missed it.
import { createDefaultLogger, LogLevels } from "@/types/console-logger";
const logger = createDefaultLogger( LogLevels.DEBUG );

// Fix round 1, Finding 3 (Important): hydrateContent runs once per item inside tight IDB-iterate
// loops (articles/trade-alerts/webinars/bonus-reports — hundreds of items per pass). Warning once
// per item would flood the console the moment any drop is live (see Finding 1). Debounce instead:
// every drop pushes the flush out by DROPPED_TICKER_WARN_DEBOUNCE_MS; a whole pass — however many
// items it touches — collapses into exactly ONE aggregate warn, shortly after the last drop in a
// contiguous burst. Deliberately module-level, sync, fire-and-forget (setTimeout) rather than a
// parameter threaded through every one of the ~11 call sites across 4 store files: it keeps
// hydrateContent's signature and return type exactly as they were, and keeps the whole fix
// contained to this one file.
const DROPPED_TICKER_WARN_DEBOUNCE_MS = 500;
let pendingDroppedTickerCount = 0;
let droppedTickerWarnTimer: ReturnType< typeof setTimeout > | undefined;

function scheduleDroppedTickerWarn( count: number ): void {
    pendingDroppedTickerCount += count;

    if ( droppedTickerWarnTimer ) {
        clearTimeout( droppedTickerWarnTimer );
    }

    droppedTickerWarnTimer = setTimeout( () => {
        const total = pendingDroppedTickerCount;
        pendingDroppedTickerCount = 0;
        droppedTickerWarnTimer = undefined;
        logger.warn( `[ content-hydration ] dropped ${ total } unresolved ticker id(s) across the last hydration pass` );
    }, DROPPED_TICKER_WARN_DEBOUNCE_MS );
}

export function hydrateContent( item: NormalizedContent, ctx: CompositionContext ): Content {
    const author = item.authorId ? ctx.getAuthorById( item.authorId ) : undefined;
    const adProduct = item.adProductId ? ctx.getProductById( item.adProductId ) : undefined;

    const categories = item.categoryIds
        .map( id => ctx.getCategoryById( id ) )
        .filter( c => c !== undefined );

    const products = item.productIds
        .map( id => ctx.getProductById( id ) )
        .filter( p => p !== undefined );

    const sectors = item.sectorIds
        .map( id => ctx.getSectorById( id ) )
        .filter( s => s !== undefined );

    // `tickerIds` carries trading symbols now, authored free-form on the content item, so it
    // resolves straight against the sentient-sourced Tickers store - the content-id -> symbol
    // crosswalk that used to sit in between is gone. Unresolved symbols are still dropped
    // silently (a typo just yields a shorter `tickers` array) but counted, so a stuck-at-zero
    // regression is visible in dev rather than only noticeable as missing ticker chips.
    const tickers = item.tickerIds
        .map( ( symbol: string ): SearchableTicker | undefined => ctx.getTickerBySymbol( symbol ) )
        .filter( ( t ): t is SearchableTicker => t !== undefined );

    const droppedTickerCount = item.tickerIds.length - tickers.length;
    if ( droppedTickerCount > 0 ) {
        scheduleDroppedTickerWarn( droppedTickerCount );
    }

    return {
        contentfulId: item.contentfulId,
        slug: item.slug,
        updatedDate: item.updatedDate,
        publishedDate: item.publishedDate,
        title: item.title,
        text: item.text,
        body2: item.body2,
        preview: item.preview,
        thumbnail: item.thumbnail,
        videoLink: item.videoLink,
        editorialAd: item.editorialAd,
        isFeatured: item.isFeatured,
        pdfLink: item.pdfLink,
        registrationUri: item.registrationUri,
        webinarDate: item.webinarDate,
        author,
        adProduct,
        categories,
        products,
        sectors,
        tickers
    };
}
